import {
    type CurrencyValueType,
    DEFAULT_UPLOAD_FILE_COUNT,
    LEGACY_TABLE_UPLOAD_FILE_COUNT,
    type TableFormColumn,
} from '@common/types/templateFormFields';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    TextField,
    useTheme,
} from '@mui/material';
import type { ChangeEvent, DragEvent } from 'react';
import { useRef } from 'react';
import UploadFileCountField from './UploadFileCountField';

export const COLUMN_TYPE_OPTIONS = [
    { label: 'Text', value: 'text' },
    { label: 'Multiline Text', value: 'textarea' },
    { label: 'Dropdown', value: 'select' },
    { label: 'Currency', value: 'currency' },
    { label: 'Percentage', value: 'percentage' },
    { label: 'Date', value: 'date' },
    { label: 'Address', value: 'address' },
    { label: 'Upload', value: 'upload' },
] as const;

interface TableColumnFieldProps {
    index: number;
    column: TableFormColumn;
    error?: { message: string; state: boolean };
    disabled?: boolean;
    onChange: (column: TableFormColumn) => void;
    onRemove: () => void;
    onDragStart: (index: number) => void;
    onDrop: (index: number) => void;
}

export default function TableColumnField({
    index,
    column,
    error,
    disabled = false,
    onChange,
    onRemove,
    onDragStart,
    onDrop,
}: TableColumnFieldProps) {
    const draggerRef = useRef<HTMLDivElement>(null);
    const columnRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const handleLabelChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...column,
            label: e.target.value,
            key: e.target.value
                .toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, ''),
        });
    };

    const handleTypeChange = (e: SelectChangeEvent) => {
        const nextType = e.target.value as TableFormColumn['type'];

        onChange({
            ...column,
            type: nextType,
            selectOptions: nextType === 'select' ? (column.selectOptions ?? []) : undefined,
            valueType: nextType === 'currency' ? column.valueType : undefined,
            typeOfDocument: nextType === 'upload' ? (column.typeOfDocument ?? '') : undefined,
            fileCount:
                nextType === 'upload' ? (column.fileCount ?? DEFAULT_UPLOAD_FILE_COUNT) : undefined,
        });
    };

    const handleValueTypeChange = (value: CurrencyValueType | '') => {
        onChange({
            ...column,
            valueType: value || undefined,
        });
    };

    const handleDocumentTypeChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({
            ...column,
            typeOfDocument: e.target.value,
        });
    };

    return (
        <Box
            component="div"
            draggable={!disabled}
            ref={columnRef}
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                background: theme.palette.neutral.white,
                padding: '4px 0',
            }}
            onDragLeave={(e) => {
                if (e.currentTarget.classList.contains('dropReady')) {
                    e.currentTarget.classList.remove('dropReady');
                }
            }}
            onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('dropReady');
            }}
            onDragStart={() => {
                onDragStart(index - 1);
            }}
            onDrop={(e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.currentTarget.classList.contains('dropReady')) {
                    e.currentTarget.classList.remove('dropReady');
                }
                onDrop(index - 1);
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: 1,
                }}
            >
                <Box
                    ref={draggerRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: disabled ? 'not-allowed' : 'grab',
                        opacity: disabled ? 0.5 : 1,
                    }}
                    onMouseDown={() => {
                        if (draggerRef.current && !disabled) {
                            draggerRef.current.style.cursor = 'grabbing';
                        }
                    }}
                    onMouseUp={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = disabled ? 'not-allowed' : 'grab';
                        }
                    }}
                    onMouseLeave={() => {
                        if (draggerRef.current) {
                            draggerRef.current.style.cursor = disabled ? 'not-allowed' : 'grab';
                        }
                    }}
                >
                    <DragIndicatorIcon sx={{ fontSize: 16 }} />
                </Box>

                <TextField
                    sx={{ flex: 1, minWidth: 150 }}
                    value={column.label || ''}
                    label={`Column ${index}`}
                    placeholder="Column name"
                    disabled={disabled}
                    error={error?.state}
                    helperText={error?.message || undefined}
                    onChange={handleLabelChange}
                />

                <FormControl sx={{ minWidth: 140 }}>
                    <Select
                        value={column.type || 'text'}
                        disabled={disabled}
                        onChange={handleTypeChange}
                    >
                        {COLUMN_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {column.type === 'currency' && (
                    <FormControl sx={{ minWidth: 140 }}>
                        <InputLabel>Value Type</InputLabel>
                        <Select
                            value={column.valueType || ''}
                            label="Value Type"
                            disabled={disabled}
                            onChange={(e) =>
                                handleValueTypeChange(e.target.value as CurrencyValueType | '')
                            }
                        >
                            <MenuItem value="">N/A</MenuItem>
                            <MenuItem value="asset">Asset</MenuItem>
                            <MenuItem value="debt">Debt</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {column.type === 'upload' && (
                    <TextField
                        sx={{ minWidth: 240 }}
                        value={column.typeOfDocument || ''}
                        label="Type of Document"
                        placeholder="Enter document type"
                        disabled={disabled}
                        onChange={handleDocumentTypeChange}
                    />
                )}

                {column.type === 'upload' && (
                    <UploadFileCountField
                        value={column.fileCount ?? LEGACY_TABLE_UPLOAD_FILE_COUNT}
                        disabled={disabled}
                        onChange={(fileCount) => onChange({ ...column, fileCount })}
                    />
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '12px',
                }}
            >
                <IconButton onClick={onRemove} disabled={disabled}>
                    <RemoveCircleOutlineIcon />
                </IconButton>
            </Box>
        </Box>
    );
}
